import React, { useCallback, useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { CustomerSearchPanel } from "../components/tabon/CustomerSearch";
import { CustomerProfileCard, LoyaltyPointsCard, RewardsWallet } from "../components/tabon/CustomerProfile";
import { ActionPanel } from "../components/tabon/ActionPanel";
import { AppLayout } from "../components/tabon/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Phone, Calendar, Beer, Award, MessageCircle, MessageCircleOff, X } from "lucide-react";
import { formatDate, segmentColor } from "../lib/format";

// Tab definitions mirrored from ActionPanel so Billing.jsx can render them in Zone A
const ALL_ACTION_TABS = [
  { key: "bill", label: "Bill Settlement", feature: "loyalty_points" },
  { key: "loyalty", label: "Loyalty", feature: "loyalty_points" },
  { key: "serve", label: "Serve from Tab", feature: "bottle_locker" },
  { key: "stamps", label: "Stamps", feature: "stamp_cards" },
];

/** Compact customer header for mobile Zone A — shows key stats only */
function CompactCustomerCard({ detail, hasFeature, onConsentToggle, onClear }) {
  const { customer, active_tabs_count = 0, active_tabs = [] } = detail;
  const bottleCount = active_tabs_count || active_tabs.length;
  const bottleLockerOn = hasFeature("bottle_locker");
  const loyaltyOn = hasFeature("loyalty_points");
  const waOptIn = customer.whatsapp_opt_in !== false; // default true for old records

  return (
    <div className="px-4 py-2 border-b border-white/5">
      {/* Name + segment badge + WA consent + clear */}
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-bold text-white text-base truncate">{customer.name}</span>
          <span
            className={`text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase tracking-wider flex-shrink-0 ${segmentColor(customer.segment)}`}
          >
            {customer.segment}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* WhatsApp consent badge */}
          <button
            type="button"
            title={waOptIn ? "WhatsApp: ON — click to opt out" : "WhatsApp: OFF — click to opt in"}
            onClick={onConsentToggle}
            className={`flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded border transition-colors ${
              waOptIn
                ? "border-green-500/40 text-green-400 bg-green-500/10 hover:bg-green-500/20"
                : "border-white/10 text-[#A0A0A0] bg-white/5 hover:bg-white/10"
            }`}
          >
            {waOptIn
              ? <><MessageCircle size={9} /> WA ✓</>
              : <><MessageCircleOff size={9} /> No WA</>
            }
          </button>
          {/* Clear customer */}
          <button
            type="button"
            onClick={onClear}
            data-testid="compact-card-clear"
            className="w-11 h-11 flex items-center justify-center rounded-lg text-[#A0A0A0] hover:text-white hover:bg-white/10 transition"
            title="Clear customer"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Phone + DOB */}
      <div className="flex items-center gap-3 text-xs text-[#A0A0A0] mt-0.5">
        <span className="flex items-center gap-1"><Phone size={10} />{customer.phone}</span>
        {customer.dob && (
          <span className="flex items-center gap-1"><Calendar size={10} />DOB {formatDate(customer.dob)}</span>
        )}
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 mt-2 text-xs">
        <div>
          <span className="text-[#A0A0A0]">Visits </span>
          <span className="text-white font-bold">{customer.total_visits}</span>
        </div>
        <div>
          <span className="text-[#A0A0A0]">Last </span>
          <span className="text-white font-semibold">
            {customer.last_visit_at ? formatDate(customer.last_visit_at) : "—"}
          </span>
        </div>
        {bottleLockerOn && (
          <div className="flex items-center gap-1">
            <Beer size={10} className="text-[#9D00FF]" />
            <span className="text-[#9D00FF] font-bold">{bottleCount}</span>
          </div>
        )}
        {loyaltyOn && (
          <div className="flex items-center gap-1">
            <Award size={10} className="text-[#9D00FF]" />
            <span className="text-[#9D00FF] font-bold">{customer.points_balance || 0}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Billing() {
  const { user, hasFeature } = useAuth();
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [activeTab, setActiveTab] = useState("bill");

  // Visible tabs based on feature flags (mirrors ActionPanel's TABS)
  const TABS = ALL_ACTION_TABS.filter(t => !t.feature || hasFeature(t.feature));

  const handleClear = useCallback(() => setSelectedId(null), []);

  const load = useCallback(async (id) => {
    if (!id) {
      setDetail(null);
      return;
    }
    try {
      const { data } = await api.get(`/customers/${id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, []);

  const toggleConsent = useCallback(async () => {
    if (!selectedId) return;
    try {
      const { data } = await api.patch(`/customers/${selectedId}/consent`);
      setDetail((prev) => prev ? {
        ...prev,
        customer: { ...prev.customer, whatsapp_opt_in: data.whatsapp_opt_in },
      } : prev);
      toast.success(data.whatsapp_opt_in ? "WhatsApp updates enabled" : "WhatsApp updates disabled");
    } catch (err) {
      toast.error(formatError(err));
    }
  }, [selectedId]);

  useEffect(() => {
    load(selectedId);
  }, [selectedId, load]);

  // Reset to first visible tab (Bill Settlement) whenever customer changes
  useEffect(() => {
    setActiveTab(TABS[0]?.key || "bill");
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const posInt = user?.venue?.pos_integration;
  const posProvider = posInt?.active
    ? (posInt.provider ? posInt.provider.charAt(0).toUpperCase() + posInt.provider.slice(1) : "your POS")
    : null;

  return (
    <AppLayout>
      {posProvider && (
        <div className="max-w-[1400px] mx-auto mb-4 bg-sky-500/10 border border-sky-500/30 rounded-xl px-4 py-3 text-sm text-sky-200">
          This venue is integrated with {posProvider}. Bills settle automatically through {posProvider} — use this screen for bottle storage only.
        </div>
      )}
      {/* ══════════════════════════════════════════
          MOBILE LAYOUT — 3-zone design (< lg)
          Zone A: fixed header (search + compact card + tabs)
          Zone B: scrollable form content
          Zone C: sticky bill footer (handled inside BillSettlementForm)
         ══════════════════════════════════════════ */}
      <div className="lg:hidden -mx-4 -mt-4 flex flex-col" style={{ minHeight: "calc(100dvh - 50px)" }}>

        {/* ── Zone A: Fixed Header ── */}
        <div className="flex-shrink-0 bg-[#0A0A0A] border-b border-white/10 z-10 sticky top-0">
          {/* Search bar */}
          <div className="px-4 pt-3 pb-2">
            <CustomerSearchPanel
              selectedId={selectedId}
              onSelect={(id) => setSelectedId(id)}
            />
          </div>

          {/* Compact customer info (only when a customer is selected) */}
          {detail && <CompactCustomerCard detail={detail} hasFeature={hasFeature} onConsentToggle={toggleConsent} onClear={handleClear} />}

          {/* Action tabs row (only when customer selected and multiple tabs) */}
          {detail && TABS.length > 1 && (
            <div className="flex gap-2 overflow-x-auto px-4 py-2 scrollbar-none">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  data-testid={`action-tab-${t.key}`}
                  onClick={() => setActiveTab(t.key)}
                  className={`px-3 py-2 rounded-lg font-semibold text-xs whitespace-nowrap transition-all min-h-[36px] flex-shrink-0 ${
                    activeTab === t.key
                      ? "bg-[#9D00FF] text-white"
                      : "bg-[#1A1A1A] border border-white/10 text-[#A0A0A0] hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Zone B: Scrollable Content ── */}
        <div className="flex-1 overflow-y-auto">
          {detail ? (
            activeTab === "loyalty" ? (
              <div className="px-4 py-4 flex flex-col gap-4">
                <LoyaltyPointsCard
                  customer={detail.customer}
                  onRefresh={() => load(selectedId)}
                />
                <RewardsWallet
                  customerId={detail.customer.id}
                  customerName={detail.customer.name}
                />
              </div>
            ) : (
              <div className="px-4 py-4">
                <ActionPanel
                  customer={detail?.customer}
                  detail={detail}
                  venue={user?.venue}
                  onRefresh={() => load(selectedId)}
                  onClear={handleClear}
                  externalTab={activeTab}
                  onExternalTabChange={setActiveTab}
                  hideTabBar
                  isMobile
                />
              </div>
            )
          ) : (
            <div className="text-center py-16 text-[#A0A0A0]">
              <Beer size={32} className="mx-auto text-[#9D00FF] mb-3 opacity-50" />
              <p className="text-sm">Search above to look up a customer.</p>
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════
          DESKTOP LAYOUT — 35/65 grid (lg+)
         ══════════════════════════════════════════ */}
      <div className="hidden lg:block max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6">
        <div className="mb-5">
          <h1 className="text-2xl md:text-3xl font-black tracking-tight">Billing Counter</h1>
          <p className="text-sm text-[#A0A0A0]">
            Fast customer lookup · One-tap tabs & serves · Visits auto-logged
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-5">
          <section className="lg:col-span-4 flex flex-col gap-4">
            <CustomerSearchPanel selectedId={selectedId} onSelect={setSelectedId} />
            <CustomerProfileCard detail={detail} onRefresh={() => load(selectedId)} onClear={handleClear} />
          </section>

          <section className="lg:col-span-8">
            <ActionPanel
              customer={detail?.customer}
              detail={detail}
              venue={user?.venue}
              onRefresh={() => load(selectedId)}
              onClear={handleClear}
            />
          </section>
        </div>
      </div>
    </AppLayout>
  );
}
